import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion /
// mood_tracker / oriora_pawline). askBrain sends the message + this app's declared action
// catalog to the generic fleet chat brain (POST /api/apps/oriora_flashcards/chat) — ONE billed
// call returns ONE registered action + a reply; the app executes it locally so decks/cards stay
// on-device. The regex intent-router below (routeMessage) is the FREE FALLBACK (offline /
// signed-out / out of funds) — chat never goes dead. Original prototype notes:
//   • "add deck Biology"                          → local parse            → add_deck
//   • "add card What is X? | The answer [to Y]"   → local parse            → add_card
//   • "make cards from my notes: … [into Y]"      → EXISTING /generate-cards → generate  (billed, study-fenced)
//   • "what's due" / "due today"                  → local counts           → due_summary
//   • "find/show cards about X"                   → local filter           → filter
//   • "delete deck X" / "delete card X"           → resolve id locally     → delete_*    (UI confirms)
//   • anything else                               → none + a short clarifying nudge
//
// STUDY-TOOL FENCE (Creem essay-mill ban — HARD, MANDATORY pre-LLM on BOTH paths). This app
// makes STUDY CARDS from the user's own notes; it NEVER writes essays, assignments, or
// homework answers. academicRefusal() runs on the raw message BEFORE serverChat is ever
// called — a do-my-homework ask is refused for FREE, so it never reaches the (billed) routing
// call either, not just the (billed) generate-cards call one step later (which keeps its own
// fence too, defence in depth — mirrors brain.ts::generateCards).

import { store, type Card } from "../store";
import { BrainError, academicRefusal, brainCall } from "../brain";
import { cardLabel, type ChatAction, type CardFilter, type FilterRow } from "./actions";
import { findNamedMatches } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry search matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; executeAction supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — "add card X and card Y" = two (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

const HELP =
  "I can add decks and cards, make cards from pasted notes, search, tell you what's due, and delete — just tell me. Try:\n" +
  '• "add deck Biology"\n' +
  '• "add card What is osmosis? | Movement of water across a membrane to Biology"\n' +
  '• "make cards from my notes: <paste> into Biology"\n' +
  '• "what\'s due" · "find cards about photosynthesis"\n' +
  '• "delete deck Biology" · "delete card What is osmosis?"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

async function findCards(term: string): Promise<Card[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listCards(undefined, { limit: 50000 });
  const hits = all.filter((c) => c.front.toLowerCase().includes(q));
  const exact = hits.filter((c) => c.front.toLowerCase() === q);
  if (exact.length) return exact;      // an exact front wins over broader includes-matches
  if (hits.length) return hits;
  // Pattern 6: nothing contains the term → fuzzy, so a misspelled card front still resolves.
  const named = findNamedMatches(all.map((c) => ({ ...c, name: c.front })), term);
  return named.map(({ name: _n, ...c }) => c as Card);
}

function pickList(rows: Card[]): string {
  return rows.slice(0, 5).map((c) => `• ${cardLabel(c)}${c.tag ? ` (${c.tag})` : ""}`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // STUDY-TOOL FENCE — pre-LLM, free, on BOTH paths. A do-my-homework ask is refused
  // deterministically before it can reach EITHER billed call (routing OR generate-cards).
  const refusal = academicRefusal(text);
  if (refusal) return { reply: "", actions: [none(refusal).action] };
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (deck/card text → id) locally, because the data
// lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.flashcards.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_deck", description: "Create a new empty deck.", params: { name: "the deck's name" } },
  { name: "add_card", description: "Add ONE card by hand (not from notes).", params: { front: "REQUIRED — the question/front side", back: "REQUIRED — the answer/back side", deck_name: "optional deck name — defaults to the user's first deck", tag: "optional short tag" } },
  { name: "delete_deck", description: "Delete a whole deck (the app asks the user to confirm first).", params: { target: "REQUIRED — which deck, by name as the user wrote it" } },
  { name: "delete_card", description: "Delete one card (the app asks the user to confirm first).", params: { target: "REQUIRED — which card, by its front text as the user wrote it" } },
  { name: "update_card", description: "EDIT an existing card — fix a typo, reword a side, change its tag. Send ONLY the fields being changed; anything you leave out is kept as it is. Use this for 'fix/change/correct/reword that card', never delete-and-re-add.", params: { target: "REQUIRED — which card, by its current front text as the user wrote it", front: "optional — the NEW front text", back: "optional — the NEW back text", tag: "optional — the NEW tag" } },
  { name: "update_deck", description: "Rename an existing deck. Use for 'rename/call this deck X'.", params: { target: "REQUIRED — which deck, by its current name", name: "REQUIRED — the new name" } },
  { name: "filter", description: "Search / list the card collection.", params: { text: "optional free text to search front/back/tag", tag: "optional exact tag to filter by", deck_name: "optional deck name to filter by", due_only: "optional true — only cards due for review" } },
  { name: "due_summary", description: "Show how many cards are due right now, per deck. No params.", params: {} },
  { name: "generate", description: "The ONE AI-billed feature: turn PASTED NOTES into study cards. Only use when the user actually pasted/gave notes text to turn into cards — never invent facts from a topic alone.", params: { notes: "REQUIRED — the user's notes text, copied verbatim from their message", deck_name: "optional deck to add the generated cards to — created if it doesn't exist" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE decks+cards collection instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function buildContext(): Promise<string> {
  try {
    const decks = await store.listDecks();
    return decks.length ? `Decks: ${decks.map((d) => `${d.name} (${d.card_count} cards)`).join(", ")}.` : "No decks yet.";
  } catch {
    return ""; // context is optional
  }
}

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asBool = (v: unknown): boolean => {
  if (typeof v === "boolean") return v;
  const s = asStr(v)?.toLowerCase();
  return s === "true" || s === "yes";
};

export async function toAction(r: ServerChat): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_deck": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What should the deck be called? Try "add deck Biology".');
      return { reply: say, action: { kind: "add_deck", name } };
    }
    case "add_card": {
      const front = asStr(p.front);
      const back = asStr(p.back);
      if (!front || !back) return none(say || 'Give me both sides — "add card What is osmosis? | Movement of water across a membrane".');
      return { reply: say, action: { kind: "add_card", deckName: asStr(p.deck_name), front, back, tag: asStr(p.tag) } };
    }
    case "delete_deck": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which deck do you want to delete?");
      const term = target.toLowerCase();
      const decks = await store.listDecks();
      const hits = decks.filter((d) => d.name.toLowerCase().includes(term));
      const exact = hits.filter((d) => d.name.toLowerCase() === term);
      const found = exact.length ? exact : hits;
      if (found.length === 0) return none(`I couldn't find a deck matching "${target}".`);
      if (found.length > 1) return none(`I found ${found.length} decks matching — be more specific:\n${found.slice(0, 5).map((d) => `• ${d.name}`).join("\n")}`);
      return { reply: say, action: { kind: "delete_deck", id: found[0].id, label: `deck "${found[0].name}" (${found[0].card_count} cards)` } };
    }
    case "delete_card": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which card do you want to delete?");
      const rows = await findCards(target);
      if (rows.length === 0) return none(`I couldn't find a card matching "${target}".`);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_card", id: rows[0].id, label: cardLabel(rows[0]) } };
    }
    case "update_card": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which card do you want to change?");
      const rows = await findCards(target);
      if (rows.length === 0) return none(`I couldn't find a card matching "${target}".`);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      const front = asStr(p.front), back = asStr(p.back), tag = asStr(p.tag);
      // Nothing named = nothing to do. Better to ask than to write an empty patch.
      if (!front && !back && tag === undefined) return none(say || "What should I change — the front, the back, or the tag?");
      const act: ChatAction = { kind: "update_card", id: rows[0].id, label: cardLabel(rows[0]) };
      if (front) act.front = front;
      if (back) act.back = back;
      if (tag !== undefined) act.tag = tag;
      return { reply: say, action: act };
    }
    case "update_deck": {
      const target = asStr(p.target);
      const name = asStr(p.name);
      if (!target) return none(say || "Which deck do you want to rename?");
      if (!name) return none(say || "What should I rename it to?");
      const term = target.toLowerCase();
      const decks = await store.listDecks();
      const hits = decks.filter((d) => d.name.toLowerCase().includes(term));
      const exact = hits.filter((d) => d.name.toLowerCase() === term);
      const found = exact.length ? exact : hits;
      if (found.length === 0) return none(`I couldn't find a deck matching "${target}".`);
      if (found.length > 1) return none(`I found ${found.length} decks matching — be more specific:\n${found.slice(0, 5).map((d) => `• ${d.name}`).join("\n")}`);
      return { reply: say, action: { kind: "update_deck", id: found[0].id, name, label: `deck "${found[0].name}"` } };
    }
    case "filter": {
      const filter: CardFilter = {};
      const text = asStr(p.text); if (text) filter.text = text;
      const tag = asStr(p.tag); if (tag) filter.tag = tag;
      const deckName = asStr(p.deck_name); if (deckName) filter.deckName = deckName;
      if (asBool(p.due_only)) filter.dueOnly = true;
      return { reply: "", action: { kind: "filter", filter, label: say || undefined } };
    }
    case "due_summary":
      return { reply: say, action: { kind: "due_summary" } };
    case "generate": {
      const notes = asStr(p.notes);
      if (!notes) return none(say || 'Paste the notes right after — e.g. "make cards from my notes: <paste>".');
      // STUDY-TOOL fence, defence in depth — the notes themselves may carry a do-my-work ask
      // even when the routing message didn't (mirrors brain.ts::generateCards).
      const refusal = academicRefusal(notes);
      if (refusal) return none(refusal);
      if (notes.length < 30) return none("Paste a bit more — a few sentences of notes is enough to work with.");
      return { reply: say, action: { kind: "generate", notes, deckName: asStr(p.deck_name) } };
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 0) The study-tool fence runs FIRST — a do-my-homework ask never reaches any branch.
  const refusal = academicRefusal(t);
  if (refusal) return none(refusal);

  // 1) GENERATE FROM NOTES — the one AI feature (EXISTING /generate-cards endpoint). Fenced.
  const genM = t.match(/^(?:make|generate|create|build)\s+(?:flash)?cards?\s+(?:from|out of|for)\s*(?:my\s+notes?)?[:\-\s]*([\s\S]+)$/i);
  if (genM) {
    let notes = genM[1].trim();
    let deckName: string | undefined;
    const intoM = notes.match(/\b(?:into|in|to)\s+(?:deck\s+)?"?([A-Za-z][\w -]{0,40})"?\s*$/);
    // Only treat a short trailing "into X" as a deck name when the notes are longer than it —
    // otherwise it's part of the notes themselves.
    if (intoM && typeof intoM.index === "number" && intoM.index > 40) {
      deckName = intoM[1].trim();
      notes = notes.slice(0, intoM.index).trim();
    }
    const genRefusal = academicRefusal(notes);
    if (genRefusal) return none(genRefusal);
    if (notes.length < 30) return none("Paste the notes right after — e.g. \"make cards from my notes: <paste>\".");
    return { reply: "", action: { kind: "generate", notes, deckName } };
  }

  // 2) ADD DECK — local parse (no server call).
  const deckM = t.match(/^(?:add|create|new|make|start)\s+(?:a\s+)?deck[:\-\s]+([\s\S]+)$/i);
  if (deckM) {
    const name = deckM[1].trim().replace(/[.?!]+$/, "");
    if (!name) return none('What should the deck be called? Try "add deck Biology".');
    // "make a deck of 4 cards about the planets" is a loosely-worded generate request, not a
    // deck name — don't create a junk-named empty deck. Point to the two real commands.
    const genShaped = /^(?:of\s+)?\d+\s+cards?\b/i.test(name) || /\bcards?\s+(?:about|on|for|covering)\b/i.test(name);
    if (genShaped) {
      const topicM = name.match(/\b(?:about|on|for|covering)\s+(?:the\s+)?(.+)$/i);
      const topic = topicM ? topicM[1].trim().replace(/[.?!]+$/, "") : "your topic";
      const deckSuggest = topic.replace(/\b\w/g, (c) => c.toUpperCase());
      return none(
        `I only make cards from notes you paste — I don't generate facts from a topic alone. Two ways:\n` +
        `• "make cards from my notes: <paste your ${topic} notes>"\n` +
        `• "add deck ${deckSuggest}" to create the empty deck, then add cards.`
      );
    }
    return { reply: "", action: { kind: "add_deck", name } };
  }

  // 3) ADD CARD — "add card <front> | <back> [to <deck>]" (also accepts "->" or "=" as the split).
  const cardM = t.match(/^(?:add|create|new)\s+(?:a\s+)?card[:\-\s]+([\s\S]+)$/i);
  if (cardM) {
    let rest = cardM[1].trim();
    let deckName: string | undefined;
    const toM = rest.match(/\s+(?:to|into|in)\s+(?:deck\s+)?"?([A-Za-z][\w -]{0,40})"?\s*$/);
    if (toM && typeof toM.index === "number") {
      deckName = toM[1].trim();
      rest = rest.slice(0, toM.index).trim();
    }
    const split = rest.split(/\s*(?:\||->|=)\s*/);
    if (split.length < 2 || !split[0].trim() || !split[1].trim()) {
      return none('Give me both sides — "add card What is osmosis? | Movement of water across a membrane to Biology".');
    }
    return { reply: "", action: { kind: "add_card", deckName, front: split[0].trim(), back: split.slice(1).join(" | ").trim() } };
  }

  // 4) WHAT'S DUE — local counts, no billed call.
  if (/\b(?:what'?s|what\s+is|anything|how\s+many\s+cards?)\s+due\b/i.test(low) || /\bdue\s+(?:today|now)\b/i.test(low) || /^due\??$/i.test(low)) {
    return { reply: "", action: { kind: "due_summary" } };
  }

  // 5) DELETE DECK — resolve the target locally; the UI asks for confirmation before executing.
  const delDeckM = t.match(/^(?:delete|remove|trash)\s+(?:the\s+)?deck\s+(?:called\s+)?([\s\S]+)$/i);
  if (delDeckM) {
    const term = delDeckM[1].trim().replace(/[.?!]+$/, "").toLowerCase();
    const decks = await store.listDecks();
    const hits = decks.filter((d) => d.name.toLowerCase().includes(term));
    const exact = hits.filter((d) => d.name.toLowerCase() === term);
    const found = exact.length ? exact : hits;
    if (found.length === 0) return none(`I couldn't find a deck matching "${delDeckM[1].trim()}".`);
    if (found.length > 1) return none(`I found ${found.length} decks matching — be more specific:\n${found.slice(0, 5).map((d) => `• ${d.name}`).join("\n")}`);
    return { reply: "", action: { kind: "delete_deck", id: found[0].id, label: `deck "${found[0].name}" (${found[0].card_count} cards)` } };
  }

  // 6) DELETE CARD — resolve by front text; single-match resolution only.
  const delCardM = t.match(/^(?:delete|remove|trash)\s+(?:the\s+)?card\s+(?:called\s+|for\s+)?([\s\S]+)$/i);
  if (delCardM) {
    const term = delCardM[1].trim();
    const rows = await findCards(term);
    if (rows.length === 0) return none(`I couldn't find a card matching "${term}".`);
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", action: { kind: "delete_card", id: rows[0].id, label: cardLabel(rows[0]) } };
  }

  // 7) FILTER / SEARCH — query-shaped → local filter over the on-device collection.
  if (/\b(show|find|list|search|which|filter)\b/i.test(low)) {
    const filter = parseFilter(t);
    return { reply: "", action: { kind: "filter", filter, label: t } };
  }

  // 8) Unclear → a short clarifying nudge.
  return none(HELP);
}

/** Build a local CardFilter from free text — tag/topic, deck, due flag. */
function parseFilter(text: string): CardFilter {
  const f: CardFilter = {};
  const low = text.toLowerCase();
  if (/\bdue\b/i.test(low)) f.dueOnly = true;
  const deckM = text.match(/\bin\s+(?:deck\s+)?"?([A-Za-z][\w -]{0,40})"?\s*$/i);
  if (deckM) f.deckName = deckM[1].trim();
  const aboutM = text.match(/\b(?:about|on|for|tagged?|containing)\s+([a-z][\w -]*)/i);
  if (aboutM) f.text = aboutM[1].trim();
  else {
    // Fall back to the words after the verb ("find osmosis cards").
    const restM = text.match(/^(?:show|find|list|search|filter)\s+(?:me\s+)?(?:all\s+)?(.+?)(?:\s+cards?)?\s*$/i);
    if (restM && restM[1] && !/^cards?$/i.test(restM[1].trim()) && !/^due/i.test(restM[1].trim())) f.text = restM[1].trim();
  }
  return f;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
